/**
 * Custom paths — canon 29 §2.1: "A Path Node is one stop on a Path — an org
 * unit (org-chart Path) or a named recipient (custom Path)."
 *
 * A custom path stores its ladder in exactly the shape an org-chart path
 * already uses: `nodes` is the ordered stop list and `nodeAssignments` names
 * who sits at each stop. Only the stop ids differ — they are synthetic and
 * point at no `org_units` row. Everything downstream (level config, cadence
 * schedule, launch, draft autosave, reopen) therefore works unchanged, so long
 * as the synthetic stops are handed to it as OrgPathNodes the way real ones are.
 *
 * Because every custom stop carries an explicit user/group, recipient
 * resolution never needs to dereference the synthetic id — `pathToLevelConfig`
 * only broadens to "anyone at this node" when neither userId nor groupId is set.
 */

import type { CatchballParticipantRef } from '@/types/prioritization/catchballVirtualConfig';
import type { CycleDesignerFormState, DesignerPath, OrgPathNode, PathMode } from '../types';

export const CUSTOM_NODE_PREFIX = 'custom:';

/** Level 0 of every custom path: the cycle owner, who holds the ball first. */
export const CUSTOM_ANCHOR_ID = 'custom:anchor';

export function isCustomNodeId(id: string): boolean {
  return id.startsWith(CUSTOM_NODE_PREFIX);
}

/** Undefined pathMode (older drafts) means the org chart, as it always did. */
export function pathModeOf(form: Pick<CycleDesignerFormState, 'pathMode'>): PathMode {
  return form.pathMode === 'custom' ? 'custom' : 'org_chart';
}

/**
 * Next free stop id for a path — one past the highest index currently on it,
 * so ids stay unique among live stops. A removed stop's id becomes available
 * again, which is safe because `removeCustomRung` clears that id's assignment
 * and window at the same time; nothing keyed to it survives to be inherited.
 */
export function nextCustomNodeId(path: DesignerPath): string {
  const prefix = `${CUSTOM_NODE_PREFIX}${path.id}:`;
  let highest = 0;
  for (const nodeId of path.nodes) {
    if (!nodeId.startsWith(prefix)) continue;
    const index = Number(nodeId.slice(prefix.length));
    if (Number.isFinite(index) && index > highest) highest = index;
  }
  return `${prefix}${highest + 1}`;
}

/** The shared level-0 stop for custom paths — the cycle owner holds the ball. */
export function customAnchorNode(label: string): OrgPathNode {
  return {
    id: CUSTOM_ANCHOR_ID,
    name: label,
    title: 'Cycle owner',
    relativeLevel: 0,
    parentId: null,
    isLeaf: false,
  };
}

/** A fresh custom path: the anchor and nothing below it yet. */
export function initialCustomPath(path: DesignerPath): DesignerPath {
  return { ...path, nodes: [CUSTOM_ANCHOR_ID], nodeAssignments: {} };
}

/**
 * The synthetic OrgPathNodes a set of custom paths implies — one per named
 * stop, positioned by its index in the ladder. Supplying these alongside the
 * anchor is what lets every existing org-chart consumer read a custom path.
 */
export function buildCustomPathNodes(paths: DesignerPath[]): OrgPathNode[] {
  const nodes: OrgPathNode[] = [];
  const seen = new Set<string>();
  for (const path of paths) {
    path.nodes.forEach((nodeId, index) => {
      if (index === 0 || !isCustomNodeId(nodeId) || seen.has(nodeId)) return;
      seen.add(nodeId);
      const assignment = path.nodeAssignments?.[nodeId];
      nodes.push({
        id: nodeId,
        name: assignment?.displayName ?? 'Unassigned',
        title: assignment ? (assignment.type === 'group' ? 'Group' : 'Person') : 'No one picked yet',
        relativeLevel: index,
        parentId: path.nodes[index - 1] ?? CUSTOM_ANCHOR_ID,
        isLeaf: index === path.nodes.length - 1,
      });
    });
  }
  return nodes;
}

/** Add a named recipient to the bottom of a custom path's ladder. */
export function appendCustomRung(path: DesignerPath, ref: CatchballParticipantRef): DesignerPath {
  const nodeId = nextCustomNodeId(path);
  return {
    ...path,
    nodes: [...path.nodes, nodeId],
    nodeAssignments: { ...(path.nodeAssignments ?? {}), [nodeId]: ref },
  };
}

/** Drop one stop from a custom path, along with its assignment and window. */
export function removeCustomRung(path: DesignerPath, nodeId: string): DesignerPath {
  if (!isCustomNodeId(nodeId) || nodeId === CUSTOM_ANCHOR_ID) return path;
  const assignments = { ...(path.nodeAssignments ?? {}) };
  delete assignments[nodeId];
  const windows = { ...(path.nodeWindows ?? {}) };
  delete windows[nodeId];
  return {
    ...path,
    nodes: path.nodes.filter((id) => id !== nodeId),
    nodeAssignments: assignments,
    nodeWindows: windows,
  };
}

/**
 * Move a stop one place up or down the ladder. The anchor is fixed at level 0,
 * so a stop can never be moved above index 1.
 */
export function moveCustomRung(
  path: DesignerPath,
  nodeId: string,
  direction: 'up' | 'down',
): DesignerPath {
  const from = path.nodes.indexOf(nodeId);
  if (from <= 0) return path;
  const to = direction === 'up' ? from - 1 : from + 1;
  if (to <= 0 || to >= path.nodes.length) return path;
  const nodes = [...path.nodes];
  [nodes[from], nodes[to]] = [nodes[to], nodes[from]];
  return { ...path, nodes };
}

/** Point an existing stop at a different user or group. */
export function assignCustomRung(
  path: DesignerPath,
  nodeId: string,
  ref: CatchballParticipantRef,
): DesignerPath {
  if (!path.nodes.includes(nodeId)) return path;
  return { ...path, nodeAssignments: { ...(path.nodeAssignments ?? {}), [nodeId]: ref } };
}

export interface CustomPathParticipants {
  /** Level 1 across every path — who the ball is tossed to first (middle tier). */
  ballRecipients: CatchballParticipantRef[];
  /** Levels 2 and below — who contributes beneath the recipients (bottom tier). */
  defaultParticipants: CatchballParticipantRef[];
}

/**
 * Flatten custom ladders into the two tiers `catchball_virtual_configs` stores,
 * so the launched cycle reads as a canon `setupMode: 'virtual'` Path to every
 * surface that resolves participants from that table.
 */
export function customPathParticipants(paths: DesignerPath[]): CustomPathParticipants {
  const ballRecipients: CatchballParticipantRef[] = [];
  const defaultParticipants: CatchballParticipantRef[] = [];
  const seen = new Set<string>();

  for (const path of paths) {
    path.nodes.forEach((nodeId, index) => {
      if (index === 0) return;
      const ref = path.nodeAssignments?.[nodeId];
      if (!ref) return;
      const key = `${ref.type}:${ref.id}`;
      if (seen.has(key)) return;
      seen.add(key);
      if (index === 1) ballRecipients.push(ref);
      else defaultParticipants.push(ref);
    });
  }

  return { ballRecipients, defaultParticipants };
}

/** Stops still waiting on a person or group — custom paths cannot launch with these. */
export function unassignedCustomStops(paths: DesignerPath[]): number {
  let count = 0;
  for (const path of paths) {
    path.nodes.forEach((nodeId, index) => {
      if (index === 0 || !isCustomNodeId(nodeId)) return;
      if (!path.nodeAssignments?.[nodeId]) count += 1;
    });
  }
  return count;
}
